/**
 * Utility functions for managing policy member benefits
 */

/**
 * Core benefit types that cannot be removed
 */
const CORE_BENEFIT_TYPES = ['Funeral', 'Accidental'];

/**
 * Determines if a benefit is a core benefit that cannot be removed
 * @param {Object} benefit - The benefit object
 * @returns {boolean} - True if core benefit
 */
export function isCoreBenefit(benefit) {
  if (!benefit) return false;

  // Check both name and type fields
  const benefitName = (benefit.name || benefit.type || '').toLowerCase();

  return CORE_BENEFIT_TYPES.some(type =>
    benefitName.includes(type.toLowerCase())
  );
}

/**
 * Gets all benefits for a member with their current selection state
 * @param {Object} member - The member object with current benefits
 * @param {Array} availableBenefits - All available benefits for this relationship
 * @returns {Array} - Benefits with selection state and core flag
 */
export function getMemberBenefitsWithState(member, availableBenefits) {
  if (!member || !member.benefits) return availableBenefits.map(b => ({ ...b, selected: false }));

  const currentBenefits = member.benefits || [];

  return availableBenefits.map(benefit => {
    // Check if this benefit is already selected
    // Match by UUID first (most accurate)
    let isSelected = false;

    for (const currentBenefit of currentBenefits) {
      // Try UUID/GUID match first
      if (benefit.uuid && (currentBenefit.uuid === benefit.uuid || currentBenefit.guid === benefit.uuid)) {
        isSelected = true;
        break;
      }

      // For benefits with same name (e.g., Group Life), match by both name AND amount
      const benefitName = (benefit.name || benefit.type || '').toLowerCase();
      const currentBenefitName = (currentBenefit.name || currentBenefit.type || '').toLowerCase();

      if (benefitName && currentBenefitName && benefitName === currentBenefitName) {
        // Also check the cover amount or premium to distinguish between variants
        const benefitAmount = benefit.cover_amount_in_cents || benefit.benefit_amount_in_cents || 0;
        const currentBenefitAmount = currentBenefit.cover_amount_in_cents || currentBenefit.benefit_amount_in_cents || 0;

        const benefitPremium = benefit.premium_amount || 0;
        const currentBenefitPremium = currentBenefit.premium_amount || 0;

        // Match if both name AND (amount OR premium) match
        if ((benefitAmount > 0 && benefitAmount === currentBenefitAmount) ||
            (benefitPremium > 0 && benefitPremium === currentBenefitPremium)) {
          isSelected = true;
          break;
        }
      }
    }

    const isCore = isCoreBenefit(benefit);

    return {
      ...benefit,
      selected: isSelected,
      isCore: isCore,
      disabled: isCore // Core benefits cannot be toggled
    };
  });
}

/**
 * Calculates total premium for selected benefits
 * @param {Array} benefits - Array of benefits with selected flag
 * @returns {number} - Total premium in cents
 */
export function calculateSelectedPremium(benefits) {
  if (!benefits || !Array.isArray(benefits)) return 0;

  return benefits.reduce((total, benefit) => {
    if (benefit.selected && benefit.premium_amount) {
      return total + benefit.premium_amount;
    }
    return total;
  }, 0);
}

/**
 * Compares two benefit lists to determine if changes were made
 * @param {Array} originalBenefits - Original member benefits
 * @param {Array} newBenefits - New benefit selection state
 * @returns {boolean} - True if changes detected
 */
export function hasChanges(originalBenefits, newBenefits) {
  if (!originalBenefits || !newBenefits) return false;

  const originalUuids = new Set(originalBenefits.map(b => b.uuid));
  const selectedNew = newBenefits.filter(b => b.selected);
  const newUuids = new Set(selectedNew.map(b => b.uuid));

  // Check if same count and same uuids
  if (originalUuids.size !== newUuids.size) return true;

  for (let uuid of originalUuids) {
    if (!newUuids.has(uuid)) return true;
  }

  return false;
}

/**
 * Filters benefits that are being added (not previously selected)
 * @param {Array} originalBenefits - Original member benefits
 * @param {Array} newBenefits - New member benefits (current state)
 * @returns {Array} - Benefits being added
 */
export function getAddedBenefits(originalBenefits, newBenefits) {
  if (!originalBenefits || !newBenefits) return [];

  const originalBenefitIds = new Set();

  // Build a set of identifiers for original benefits
  originalBenefits.forEach(b => {
    // Use UUID as primary identifier
    if (b.uuid) {
      originalBenefitIds.add(b.uuid);
    }
    // Also create a composite key for name+amount matching
    const benefitName = (b.name || b.type || '').toLowerCase();
    const benefitAmount = b.cover_amount_in_cents || b.benefit_amount_in_cents || 0;
    const benefitPremium = b.premium_amount || (b.additional_details && b.additional_details.premium_amount) || 0;
    if (benefitName) {
      originalBenefitIds.add(`${benefitName}:${benefitAmount}:${benefitPremium}`);
    }
  });

  // Filter new benefits that aren't in original
  return newBenefits.filter(b => {
    if (!b) return false;

    // Check UUID match
    if (b.uuid && originalBenefitIds.has(b.uuid)) {
      return false;
    }

    // Check composite key match
    const benefitName = (b.name || b.type || '').toLowerCase();
    const benefitAmount = b.cover_amount_in_cents || b.benefit_amount_in_cents || 0;
    const benefitPremium = b.premium_amount || (b.additional_details && b.additional_details.premium_amount) || 0;
    const compositeKey = `${benefitName}:${benefitAmount}:${benefitPremium}`;

    if (originalBenefitIds.has(compositeKey)) {
      return false;
    }

    // This is a new benefit - only include if it's not a core benefit
    return !isCoreBenefit(b);
  });
}

/**
 * Filters benefits that are being removed (previously selected)
 * @param {Array} originalBenefits - Original member benefits
 * @param {Array} newBenefits - New member benefits (current state)
 * @returns {Array} - Benefits being removed
 */
export function getRemovedBenefits(originalBenefits, newBenefits) {
  if (!originalBenefits || !newBenefits) return [];

  const newBenefitIds = new Set();

  // Build a set of identifiers for new benefits
  newBenefits.forEach(b => {
    // Use UUID as primary identifier
    if (b.uuid) {
      newBenefitIds.add(b.uuid);
    }
    // Also create a composite key for name+amount matching
    const benefitName = (b.name || b.type || '').toLowerCase();
    const benefitAmount = b.cover_amount_in_cents || b.benefit_amount_in_cents || 0;
    const benefitPremium = b.premium_amount || (b.additional_details && b.additional_details.premium_amount) || 0;
    if (benefitName) {
      newBenefitIds.add(`${benefitName}:${benefitAmount}:${benefitPremium}`);
    }
  });

  // Filter original benefits that aren't in new
  return originalBenefits.filter(b => {
    if (!b || isCoreBenefit(b)) return false;

    // Check UUID match
    if (b.uuid && newBenefitIds.has(b.uuid)) {
      return false;
    }

    // Check composite key match
    const benefitName = (b.name || b.type || '').toLowerCase();
    const benefitAmount = b.cover_amount_in_cents || b.benefit_amount_in_cents || 0;
    const benefitPremium = b.premium_amount || (b.additional_details && b.additional_details.premium_amount) || 0;
    const compositeKey = `${benefitName}:${benefitAmount}:${benefitPremium}`;

    if (newBenefitIds.has(compositeKey)) {
      return false;
    }

    // This benefit was removed
    return true;
  });
}
